import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useSearchUsers, useToggleProMembership, useToggleRole } from '@/hooks/useAdminUsers';
import { UserFilters } from '@/components/admin/UserFilters';
import { UsersTable } from '@/components/admin/UsersTable';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AdminUsers = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filterPro, setFilterPro] = useState<boolean | null>(null);
  const [filterRole, setFilterRole] = useState<'admin' | 'editor' | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isError, error } = useSearchUsers({
    search,
    filterPro,
    filterRole,
    page,
    pageSize,
  });

  const toggleProMutation = useToggleProMembership();
  const toggleRoleMutation = useToggleRole();

  const users = data?.users ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [isAdmin, loading, navigate]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterProChange = (value: boolean | null) => {
    setFilterPro(value);
    setPage(1);
  };

  const handleFilterRoleChange = (value: 'admin' | 'editor' | null) => {
    setFilterRole(value);
    setPage(1);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const handleToggleProMembership = (userId: string, currentStatus: boolean) => {
    toggleProMutation.mutate({ userId, currentStatus });
  };

  const handleToggleRole = (userId: string, role: 'admin' | 'editor', hasRole: boolean) => {
    toggleRoleMutation.mutate({ userId, role, hasRole });
  };

  const sendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/admin/login`,
      });

      if (error) throw error;

      toast.success(`Password reset email sent to ${email}`);
    } catch (error: any) {
      toast.error('Failed to send password reset email');
      console.error(error);
    }
  };

  if (loading || (isLoading && page === 1)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">User Management</h1>
          <p className="text-muted-foreground">
            Search, filter, and manage user roles and memberships
          </p>
        </div>

        <div className="space-y-4">
          <UserFilters
            search={search}
            onSearchChange={handleSearchChange}
            filterPro={filterPro}
            onFilterProChange={handleFilterProChange}
            filterRole={filterRole}
            onFilterRoleChange={handleFilterRoleChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />

          {isError && (
            <div className="text-destructive text-sm p-4 border border-destructive rounded-md">
              Error loading users: {error?.message}
            </div>
          )}

          <UsersTable
            users={users}
            isLoading={isLoading}
            onTogglePro={handleToggleProMembership}
            onToggleRole={handleToggleRole}
            onSendPasswordReset={sendPasswordReset}
          />

          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {totalCount > 0
                ? `Showing ${from}–${to} of ${totalCount} users`
                : 'No users found'}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Prev
              </Button>
              <span className="text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminUsers;
